import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, MessageSquare } from 'lucide-react';
import { talents } from '../../services/apiService';
import { Modal, Button, TextField, Spinner } from '@anyemp/ui-kit';
import { formatApiError } from '../../utils/formatApiError';
import { getTalentTypeLabel } from '../../utils/talentTypeUtils';

/** Must match Joi stageUpdateSchema.reason.max in src/middleware/validation.js */
const STAGE_REASON_MAX_LENGTH = 500;

/**
 * StageTransitionModal
 *
 * Проще, чем StatusTransitionModal: у стадий нет графа переходов
 * (talent_status_transitions на стадии не распространяется) и нет логики
 * дат ухода. Единственное правило — причина обязательна только при движении
 * назад по воронке (stage_order уменьшается), и сервер это проверяет сам.
 *
 * Второе правило — стадия задаёт тип таланта: перевод на стадию другого типа
 * меняет тип (и talent_id) и тоже требует причины.
 *
 * Поэтому подход "оптимистичный": сразу пробуем перевести без причины;
 * если сервер ответит, что причина нужна, показываем поле.
 */
const StageTransitionModal = ({
  isOpen,
  onClose,
  talentId,
  talentName,
  fromStage,
  toStage,
  onSuccess,
  onError
}) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // 'check' — первая попытка без причины, 'reason' — причина обязательна, 'success', 'error'
  const [step, setStep] = useState('check');

  // Стадия другого типа меняет тип таланта. Типы стадий есть не у всех
  // вызывающих, поэтому запоминаем и ответ сервера
  const [serverSaysTypeChange, setServerSaysTypeChange] = useState(false);
  const knownTypeChange = Boolean(
    fromStage?.talent_type && toStage?.talent_type && fromStage.talent_type !== toStage.talent_type
  );
  const typeChanging = knownTypeChange || serverSaysTypeChange;
  const typeChangeTitle = knownTypeChange
    ? `This changes the talent type: ${getTalentTypeLabel(fromStage.talent_type)} → ${getTalentTypeLabel(toStage.talent_type)}`
    : 'This changes the talent type';

  useEffect(() => {
    if (isOpen) {
      executeTransition(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fromStage, toStage]);

  const executeTransition = async (providedReason) => {
    if (!toStage) {
      setError('Missing stage information');
      setStep('error');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await talents.updateTalentStage(talentId, {
        stage_id: toStage.id,
        ...(providedReason ? { reason: providedReason } : {})
      });

      if (response && response.data) {
        setStep('success');
        if (onSuccess) onSuccess(response.data);
      } else {
        throw new Error('Unexpected server response');
      }
    } catch (err) {
      console.error('Error executing stage transition:', err);

      const reasonRequired = err.response?.data?.error === 'Reason required';
      if (reasonRequired) {
        setServerSaysTypeChange(String(err.response?.data?.message || '').includes('talent type will change'));
        setStep('reason');
        setError(null);
      } else {
        setError(formatApiError(err, `Error changing stage: ${err.message || 'Unknown error'}`));
        setStep('error');
        if (onError) onError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReason = () => {
    if (!reason.trim()) {
      setError(typeChanging
        ? 'Please explain why the talent type is changing (required)'
        : 'Please explain why this talent is moving back a stage (required)');
      return;
    }
    executeTransition(reason);
  };

  const handleClose = () => {
    setStep('check');
    setReason('');
    setError(null);
    setServerSaysTypeChange(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} onClose={handleClose} size="small" showCloseButton>
      <div className="pr-7 mb-4">
        <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">Stage Change</h3>
      </div>

      <div>
        <div className="mb-6">
          <h4 className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-2">{talentName}</h4>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: fromStage?.color || '#6B7280' }} />
              <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                {fromStage?.name || 'No stage'}
              </span>
            </div>
            <span className="text-light-text-tertiary dark:text-dark-text-tertiary">→</span>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: toStage?.color || '#6B7280' }} />
              <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{toStage?.name}</span>
            </div>
          </div>
        </div>

        {step === 'check' && (
          <div className="flex justify-center items-center py-8">
            <Spinner size="large" color="primary" />
          </div>
        )}

        {step === 'reason' && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <h5 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">
                  {typeChanging ? typeChangeTitle : 'This moves the talent back a stage'}
                </h5>
                <p className="text-xs text-blue-700 dark:text-blue-400">
                  {typeChanging
                    ? `"${toStage?.name}" is a stage of another talent type. The talent ID is regenerated and the status is reset to the default of the new stage. Please explain why — type changes are logged with a reason`
                    : 'Please explain why — backward moves are logged with a reason'}
                </p>
              </div>
            </div>

            <TextField
              label="Reason"
              required
              multiline
              rows={3}
              fullWidth
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={STAGE_REASON_MAX_LENGTH}
              placeholder={typeChanging ? 'Why is the talent type changing?' : 'Why is this talent going back to an earlier stage?'}
              autoFocus
              helperText={`${reason.length}/${STAGE_REASON_MAX_LENGTH}`}
            />
          </div>
        )}

        {step === 'success' && (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
              Stage Successfully Changed!
            </h3>
          </div>
        )}

        {step === 'error' && (
          <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <h5 className="text-sm font-medium text-red-900 dark:text-red-300 mb-1">Stage Change Failed</h5>
              <p className="text-xs text-red-700 dark:text-red-400">{error}</p>
            </div>
          </div>
        )}

        {error && step === 'reason' && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}
      </div>

      <div className="mt-2 flex flex-col-reverse gap-3 border-t border-light-border-primary pt-6 dark:border-dark-border-primary sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
        {step === 'check' && (
          <Button type="button" variant="outlined" color="neutral" onClick={handleClose}>
            Cancel
          </Button>
        )}

        {step === 'reason' && (
          <>
            <Button type="button" variant="outlined" color="neutral" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="button" color="primary" onClick={handleSubmitReason} disabled={!reason.trim() || loading}>
              {loading ? 'Changing…' : 'Change Stage'}
            </Button>
          </>
        )}

        {step === 'success' && (
          <Button type="button" color="success" onClick={handleClose}>
            Close
          </Button>
        )}

        {step === 'error' && (
          <>
            <Button type="button" variant="outlined" color="neutral" onClick={() => executeTransition(null)} disabled={loading}>
              Try Again
            </Button>
            <Button type="button" color="neutral" onClick={handleClose}>
              Close
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default StageTransitionModal;
